import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { coversFullMonth, getFirstDayOfTheMonth, toLocalHyphenatedDateString } from '../../../../shared/convert-dates';
import { getPickedMissingDates } from '../../../../shared/get-picked-missing-dates';
import { hasOverlappingDates } from '../../../../shared/date-utils';

dayjs.extend(customParseFormat);

const DATE_MUST_BE_BEETWEEN = 'Date exceeds availability range!';
const DATE_MUST_BE_CORRECT = 'Date must have format yyyy-mm!';
const DAY_ENTITLEMENT_DATE_MUST_BE_CORRECT = 'Day entitlement date must have format yyyy-mm-dd!';
const DAY_ENTITLEMENT_CANNOT_COVER_FULL_MONTH = 'Day entitlement cannot cover a full month!';
const EXPIRY_DATE_MUST_BE_CORRECT = 'Expiry date must be today or later and have format yyyy-mm-dd!';
const START_DATE_MUST_BE_EARLIER = 'Start date must be before end date!';
const END_DATE_MUST_BE_PAST = 'End date must be before start date!';
const MAX_DATE_MUST_BE_FUTURE = 'Max ongoing date must be after end date!';
const OVERLAPPING_DATES = 'Selected date range overlaps with another entitlement.';

const getMissingDatesError = (rangeMissingChunks, chunkType) => {
  const missingDateDisplayList =
    rangeMissingChunks.length <= 5 ? rangeMissingChunks.join(', ') : `${rangeMissingChunks.slice(0, 5).join(', ')}, and more`;

  const plural = rangeMissingChunks.length > 1;
  let typeDescription;
  if (chunkType === 'days') {
    typeDescription = `day${plural ? 's' : ''}`;
  }
  if (chunkType === 'months') {
    typeDescription = `month${plural ? 's' : ''}`;
  }
  return `
    There ${plural ? 'are' : 'is'} ${rangeMissingChunks.length} missing ${typeDescription} in the selected range
    (${missingDateDisplayList}).
  `;
};

export const getAvailabilityDates = (
  { availabilityStartDate, availabilityEndDate, availabilityEndMonth, missingDays, missingMonths },
  isDayEntitlement
) => {
  return {
    availableStartDate: new Date(isDayEntitlement ? availabilityStartDate : getFirstDayOfTheMonth(availabilityStartDate)),
    availableEndDate: new Date(isDayEntitlement ? availabilityEndDate : getFirstDayOfTheMonth(availabilityEndMonth)),
    missingDays,
    missingMonths,
  };
};

export const validateDateInput = ({
  value,
  availableStartDate,
  availableEndDate,
  isDayEntitlement,
  key,
  oppositeDate,
  missingDays,
  missingMonths,
  sameFeeds = [],
}) => {
  const isStartDate = key === 'startDate';
  const [startDate, endDate] = isStartDate ? [value, oppositeDate] : [oppositeDate, value];
  if (oppositeDate && startDate > endDate) {
    return isStartDate ? START_DATE_MUST_BE_EARLIER : END_DATE_MUST_BE_PAST;
  }

  const { chunks: pickedRangeMissingDates, chunkType } = getPickedMissingDates({
    isDayEntitlement,
    missingMonths,
    missingDays,
    startDate,
    endDate,
  });
  if (isStartDate && pickedRangeMissingDates.length) {
    return getMissingDatesError(pickedRangeMissingDates, chunkType);
  }

  const dateFormat = isDayEntitlement ? 'YYYY-MM-DD' : 'YYYY-MM';
  const pickedDate = isDayEntitlement ? new Date(value) : new Date(getFirstDayOfTheMonth(value));
  if (!dayjs(value, dateFormat, true).isValid()) {
    return isDayEntitlement ? DAY_ENTITLEMENT_DATE_MUST_BE_CORRECT : DATE_MUST_BE_CORRECT;
  }

  const isValid = pickedDate >= availableStartDate && pickedDate <= availableEndDate;
  if (!isValid) {
    return DATE_MUST_BE_BEETWEEN;
  }

  if (isDayEntitlement && startDate && endDate && coversFullMonth(startDate, endDate)) {
    return DAY_ENTITLEMENT_CANNOT_COVER_FULL_MONTH;
  }

  const isOverlapping = sameFeeds.some((feed) => {
    const { startDate: feedStartDate, endDate: feedEndDate } = feed;
    return hasOverlappingDates({ startDate, endDate }, { startDate: feedStartDate, endDate: feedEndDate });
  });
  if (isOverlapping) {
    return OVERLAPPING_DATES;
  }
};

export const validateExpiryDate = (value) => {
  const dateIsIncorrect = !dayjs(value, 'YYYY-MM-DD', true).isValid() || value < toLocalHyphenatedDateString(new Date());
  return dateIsIncorrect ? EXPIRY_DATE_MUST_BE_CORRECT : null;
};

export const validateMaxDate = (maxDate, endDate) => {
  if (!dayjs(maxDate, 'YYYY-MM', true).isValid()) {
    return DATE_MUST_BE_CORRECT;
  }

  if (maxDate <= endDate) {
    return MAX_DATE_MUST_BE_FUTURE;
  }

  return null;
};
